import axios, { isAxiosError } from "axios";
import {
  AccountProfile,
  AdminContext,
  AUTH_STATION_SLUG_HEADER,
  Booking,
  BookingEndRequest,
  BookingEndResponse,
  BookingFeedbackRequest,
  BookingStartRequest,
  CampingCaptureResponse,
  CampingCheckoutRequest,
  CampingCheckoutResponse,
  CampingQuote,
  CampingQuoteRequest,
  CampingStation,
  CampingStationOption,
  Interface,
  MeteredCheckoutCaptureResponse,
  MeteredCheckoutRequest,
  MeteredCheckoutResponse,
  MeteredQuote,
  MonthlyExportRequest,
  RegisterRequestDto,
  PromoteStationAdminRequest,
  StationLocationDto,
  StationAdmin,
  UpdateStationAdminStationsRequest,
  Unit,
  UnlockLocksOfBookingResponse,
} from "use-smart-locks-shared";
import { authClient } from "../../authentication/core/client/auth-client";
import { env } from "../../env";
import { queryClient } from "./query-client";

if (!env.nestApiUrl) {
  throw new Error("VITE_NEST_API_URL is not defined");
}

const api = axios.create({
  baseURL: env.nestApiUrl,
});

api.interceptors.request.use(async (config) => {
  const authToken = await authClient.getAuthToken();
  if (!authToken) {
    return config;
  }

  config.headers.set("Authorization", `Bearer ${authToken}`);

  return config;
});

export const registerAccount = (
  data: RegisterRequestDto & {
    stationSlug?: string;
  },
): Promise<void> =>
  api
    .post<{ status: true }>(
      "/auth/register",
      {
        name: data.name,
        email: data.email,
        password: data.password,
        phoneNumber: data.phoneNumber,
        street: data.street,
        houseNumber: data.houseNumber,
        postalCode: data.postalCode,
        city: data.city,
      },
      data.stationSlug
        ? {
            headers: {
              [AUTH_STATION_SLUG_HEADER]: data.stationSlug,
            },
          }
        : undefined,
    )
    .then(() => undefined);

export const getAccountProfile = (): Promise<AccountProfile> =>
  api.get<AccountProfile>("/auth/profile").then((response) => response.data);

export const getAdminContext = (): Promise<AdminContext> =>
  api.get<AdminContext>("/admin/context").then((response) => response.data);

export const getStationAdmins = (): Promise<StationAdmin[]> =>
  api.get<StationAdmin[]>("/admin/admins").then((response) => response.data);

export const promoteStationAdmin = (
  data: PromoteStationAdminRequest,
): Promise<StationAdmin> =>
  api
    .post<StationAdmin>("/admin/admins", data)
    .then((response) => response.data);

export const updateStationAdminStations = (
  userId: string,
  data: UpdateStationAdminStationsRequest,
): Promise<StationAdmin> =>
  api
    .put<StationAdmin>(
      `/admin/admins/${encodeURIComponent(userId)}/stations`,
      data,
    )
    .then((response) => response.data);

export const demoteStationAdmin = (userId: string): Promise<void> =>
  api
    .delete(`/admin/admins/${encodeURIComponent(userId)}`)
    .then(() => undefined);

const getFileNameFromContentDisposition = (
  headerValue: string | undefined,
): string | undefined => {
  if (!headerValue) {
    return undefined;
  }

  const utf8Match = /filename\*=UTF-8''([^;]+)/i.exec(headerValue);
  if (utf8Match?.[1]) {
    return decodeURIComponent(utf8Match[1]);
  }

  const basicMatch = /filename="?([^";]+)"?/i.exec(headerValue);
  return basicMatch?.[1];
};

export const downloadMonthlyCsvExport = async (
  data: MonthlyExportRequest,
): Promise<{ blob: Blob; fileName: string }> => {
  const response = await api.get<Blob>("/exports/monthly-csv-zip", {
    headers: {
      Accept: "application/zip",
    },
    params: data,
    responseType: "blob",
  });

  return {
    blob: response.data,
    fileName:
      getFileNameFromContentDisposition(
        response.headers["content-disposition"] as string | undefined,
      ) ?? `${data.stationSlug}-${data.period}-monthly-export.zip`,
  };
};

export const getApiUserErrorMessage = (error: unknown): string | undefined => {
  if (!isAxiosError(error)) {
    return undefined;
  }

  const data = error.response?.data as unknown;

  if (
    data &&
    typeof data === "object" &&
    "error" in data &&
    "message" in data
  ) {
    // ignore errors that only have a "message" but not an "error" field since those are defaults like "Bad Request"

    return data.message?.toString();
  }

  return undefined;
};

export const isUnauthorizedApiError = (error: unknown): boolean =>
  isAxiosError(error) && error.response?.status === 401;

export const isForbiddenApiError = (error: unknown): boolean =>
  isAxiosError(error) && error.response?.status === 403;

export const getStationLocations = (): Promise<StationLocationDto[]> =>
  api.get<StationLocationDto[]>("/station-locations").then((r) => r.data);

export const getCampingStations = (): Promise<CampingStationOption[]> =>
  api.get<CampingStationOption[]>("/camping/stations").then((r) => r.data);

export const getCampingStationBySlug = (
  stationSlug: string,
): Promise<CampingStation> =>
  api
    .get<CampingStation>("/camping/stations/get-by-slug", {
      params: { slug: stationSlug },
    })
    .then((response) => response.data);

export const getCampingQuote = (
  stationId: string,
  data: Interface<CampingQuoteRequest>,
): Promise<CampingQuote> =>
  api
    .post<CampingQuote>(
      `/camping/stations/${encodeURIComponent(stationId)}/checkout/quote`,
      data,
    )
    .then((response) => response.data);

export const getCampingCheckoutUrl = (
  stationId: string,
  data: Interface<CampingCheckoutRequest>,
): Promise<CampingCheckoutResponse> =>
  api
    .post<CampingCheckoutResponse>(
      `/camping/stations/${encodeURIComponent(stationId)}/checkout`,
      data,
    )
    .then((response) => response.data);

export const captureCampingCheckout = (
  stationId: string,
  checkoutId: string,
): Promise<CampingCaptureResponse> =>
  api
    .post<CampingCaptureResponse>(
      `/camping/stations/${encodeURIComponent(stationId)}/checkout/${encodeURIComponent(checkoutId)}/capture`,
    )
    .then((response) => response.data);

export const getBookingBySlug = (bookingSlug: string): Promise<Booking> =>
  api
    .get<Booking>(`/bookings/get-by-slug`, {
      params: {
        slug: bookingSlug,
      },
    })
    .then((response) => response.data);

export const getBookingsByDeviceId = (deviceId: string): Promise<Booking[]> =>
  api
    .get<Booking[]>(`/bookings/get-by-device-id`, {
      params: {
        deviceId,
      },
    })
    .then((response) => response.data);

export const unlockLocksOfBooking = async (
  bookingId: string,
  data?: BookingStartRequest,
): Promise<UnlockLocksOfBookingResponse> => {
  const result = await api
    .post<UnlockLocksOfBookingResponse>(
      `/bookings/${encodeURIComponent(bookingId)}/unlock`,
      data,
    )
    .then((response) => response.data);
  await invalidateBookings();
  return result;
};

export const endBooking = async (
  bookingId: string,
  data?: BookingEndRequest,
) => {
  const result = await api
    .post<BookingEndResponse>(
      `/bookings/${encodeURIComponent(bookingId)}/end`,
      data,
    )
    .then((response) => response.data);
  await invalidateBookings();
  return result;
};

export const submitBookingFeedback = async (
  bookingId: string,
  data: BookingFeedbackRequest,
): Promise<void> => {
  await api
    .post<never>(`/bookings/${encodeURIComponent(bookingId)}/feedback`, data)
    .then((response) => response.data);
  await invalidateBookings();
};

const invalidateBookings = async () => {
  /**
   * Make sure that e.g. startedAt date is updated.
   * We can invalidate specific bookings in the future (by passing the slug), but for now we just invalidate all.
   */
  await queryClient.invalidateQueries({
    queryKey: ["booking"],
  });
};

export const getUnitBySlug = (unitSlug: string): Promise<Unit> =>
  api
    .get<Unit>("/metered/units/get-by-slug", {
      params: { slug: unitSlug },
    })
    .then((response) => response.data);

export const getMeteredQuote = (unitId: string): Promise<MeteredQuote> =>
  api
    .get<MeteredQuote>(
      `/metered/units/${encodeURIComponent(unitId)}/checkout/quote`,
    )
    .then((response) => response.data);

export const getMeteredCheckoutUrl = (
  unitId: string,
  data?: MeteredCheckoutRequest,
): Promise<MeteredCheckoutResponse> =>
  api
    .post<MeteredCheckoutResponse>(
      `/metered/units/${encodeURIComponent(unitId)}/checkout`,
      data,
    )
    .then((response) => response.data);

export const captureMeteredCheckout = (
  unitId: string,
  checkoutId: string,
): Promise<MeteredCheckoutCaptureResponse> =>
  api
    .post<MeteredCheckoutCaptureResponse>(
      `/metered/units/${encodeURIComponent(unitId)}/checkout/${encodeURIComponent(checkoutId)}/capture`,
    )
    .then((response) => response.data);
